<template>
  <container v-bind="container" :leading="_leading">
    <input
      v-model="currentValue"
      type="text"
      class="flex-grow px-2 h-full w-full bg-transparent text-sm"
      :max="max"
      :min="min"
      :step="step"
      v-bind="attributes"
      v-on="handlers"
      @keypress="handleKeyPress"
      @focusout="handleFocusOut"
      @input="handleInput"
    >

    <template #leading>
      <slot name="leading" />
    </template>

    <template #trailing>
      <slot name="trailing" />
    </template>
  </container>
</template>

<script>
import Container from './Container'

import classes from '@/mixins/fields/classes.js'
import attributes from '@/mixins/fields/attributes.js'
import methods from '@/mixins/fields/methods.js'
import input from '@/mixins/fields/input.js'

export default {
  components: {
    Container,
  },
  mixins: [attributes, classes, methods, input],
  props: {
    value: {
      type: [Number, String],
      default: 0,
    },
    increment: {
      type: Number,
      default: 1,
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 99999999999999999,
    },
    step: {
      type: String,
      default: 'any',
    },
    decimalPlaces: {
      type: Number,
      default: 2,
    },
    slim: {
      type: Boolean,
      default: false,
    },
    leading: {
      type: String,
      default: null,
    },
    defaultSizeClass: {
      type: [String, Object, Array],
      default: 'p-1',
    },
    largeSizeClass: {
      type: [String, Object, Array],
      default: 'p-4 text-lg',
    },
    smallSizeClass: {
      type: [String, Object, Array],
      default: 'p-px text-sm',
    },
  },
  computed: {
    currentValue: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      },
    },
    _leading () {
      if (this.leading) {
        return this.leading
      }

      return this.$auth.info.location.currencySymbol
    },
    _decimalPlaces () {
      const value = Number(this.decimalPlaces)
      if (!Number.isFinite(value)) {
        return 2
      }

      return Math.min(100, Math.max(0, Math.floor(value)))
    },
  },
  methods: {
    handleInput (e) {
      const value = Number(e.target.value)
      if (value < this.min) {
        e.target.value = this.min
        this.currentValue = this.min
      } else if (value > this.max) {
        e.target.value = this.max
        this.currentValue = this.max
      }
    },
    handleKeyPress (e) {
      const keyCode = (e.keyCode ? e.keyCode : e.which)
      const value = e.target.value
      const i = value.indexOf('.')
      const decimalPart = value.split('.')[1] || ''
      const isCursorInDecimal = e.target.selectionStart > i

      if ((keyCode < 48 || keyCode > 57) && (keyCode !== 46 || i !== -1 || this._decimalPlaces === 0)) {
        // only allow numbers and one dot
        e.preventDefault()
      } else if (
        keyCode !== 46 &&
        e.target.selectionStart === e.target.selectionEnd &&
        i > -1 &&
        isCursorInDecimal &&
        decimalPart.length >= this._decimalPlaces
      ) {
        // restrict decimal places
        e.preventDefault()
      }
    },
    handleFocusOut ($event) {
      $event.target.value = (Number($event.target.value) || 0).toFixed(this._decimalPlaces)
    },
  },
}
</script>

<style>
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
